import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthField, AuthShell, FormError } from "@/components/AuthForm";
import { GoogleButton } from "@/components/GoogleAuth";
import { LegalConsent, PrivacyNotice } from "@/components/LegalConsent";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", accepted_terms: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (key: "email" | "password") => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres");
    if (!form.accepted_terms) return setError("Acepta los Términos y el Aviso de privacidad para crear tu cuenta");
    setLoading(true);
    try {
      await register(form);
      navigate("/suscripcion?auto=1", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Crea tu cuenta" subtitle="Empieza a administrar tu dinero con Otium.">
      <div className="space-y-5" data-testid="register-form">
        <GoogleButton accepted={form.accepted_terms} onError={setError} back="/registro" testId="register-google-button" />
        <LegalConsent checked={form.accepted_terms} onChange={(v) => setForm((f) => ({ ...f, accepted_terms: v }))} testId="register-accept-terms" />
        <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-[#DDE1E8]" />o con tu correo<span className="h-px flex-1 bg-[#DDE1E8]" /></div>
        <form onSubmit={onSubmit} className="space-y-5">
          <AuthField id="register-email" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
          <AuthField id="register-password" label="Contraseña" type="password" value={form.password} onChange={set("password")} autoComplete="new-password" />
          <p className="text-xs text-slate-400">Mínimo 8 caracteres.</p>
          <FormError message={error} testId="register-error" />
          <Button type="submit" disabled={loading} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="register-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear cuenta y continuar al pago"}
          </Button>
        </form>
      </div>
      <PrivacyNotice />
      <p className="mt-6 text-center text-sm text-slate-500">
        ¿Ya tienes cuenta?{" "}
        <Link to="/login" className="font-semibold text-[#1B2A4A] underline-offset-4 hover:underline" data-testid="go-to-login-link">Inicia sesión</Link>
      </p>
    </AuthShell>
  );
}
